import { useTeacherUnsavedChanges } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { hooksV2Api, lessonPlansApi } from '../../../services/apiV2';
import { Hook, LessonPlan } from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { PageHeader } from '../../../components/layout/PageHeader';
import { SectionCard } from '../../../components/layout/SectionCard';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { lessonResourceSourceLabel } from '../../../utils/lessonResources';

const STAGES = ['why', 'define', 'do', 'explain', 'apply', 'reflect'] as const;

type RepositoryLesson = {
  key: string;
  sequence: string;
  title: string;
  description: string;
  project_key: string;
  project_title: string;
  curriculum_status: 'active' | 'draft' | 'archived';
  lesson_status: 'ready' | 'draft' | 'deprecated';
  cycle: string;
  week_number: number | null;
  importable: boolean;
  can_add: boolean;
  step_count: number;
  content_format: 'lesson' | 'activity';
  activity_type: string;
  activity_types: string[];
  status: 'available' | 'current' | 'update_available';
  lesson_plan_id: number | null;
  lesson_plan_archived: boolean;
  source_hash: string;
  resource_summary: RepositoryResourceSummary;
};

type RepositoryMeeting = {
  key: string;
  project_key: string;
  title: string;
  week_number: number | null;
  all_imported: boolean;
  items: Array<RepositoryLesson & { source_key: string; role: string; required: boolean; availability: string }>;
};

type RepositoryResourcePreviewItem = {
  title: string;
  resource_type: string;
  source_type: string;
  audience: 'teacher' | 'student' | 'both';
  needs_printing: boolean;
};

type RepositoryResourceSummary = {
  incoming_count: number;
  printable_count: number;
  teacher_only_count: number;
  student_accessible_count: number;
  source_types: string[];
  has_changes: boolean;
  added: RepositoryResourcePreviewItem[];
  removed: RepositoryResourcePreviewItem[];
  changed: RepositoryResourcePreviewItem[];
};

type RepositoryProject = {
  project_key: string;
  project_title: string;
  grade_level: string;
  school_year: string;
  curriculum_status: 'active' | 'draft' | 'archived';
  lesson_count: number;
};

type RepositoryWeekOption = {
  key: string;
  label: string;
  lessons: RepositoryLesson[];
};

const repositoryLessonWeekKey = (lesson: RepositoryLesson) => (
  Number.isInteger(lesson.week_number) && Number(lesson.week_number) >= 1
    ? `week-${lesson.week_number}`
    : 'week-not-set'
);

const repositoryWeekOptions = (lessons: RepositoryLesson[]): RepositoryWeekOption[] => {
  const groups = new Map<string, RepositoryWeekOption>();
  lessons.forEach((lesson) => {
    const key = repositoryLessonWeekKey(lesson);
    const label = key === 'week-not-set' ? 'Week not set' : `Week ${lesson.week_number}`;
    const existing = groups.get(key);
    if (existing) {
      existing.lessons.push(lesson);
    } else {
      groups.set(key, { key, label, lessons: [lesson] });
    }
  });
  return Array.from(groups.values()).sort((left, right) => {
    if (left.key === right.key) return 0;
    if (left.key === 'week-not-set') return 1;
    if (right.key === 'week-not-set') return -1;
    return Number(left.key.replace('week-', '')) - Number(right.key.replace('week-', ''));
  });
};

type LessonWeekGroup = {
  key: string;
  label: string;
  plans: LessonPlan[];
};

const lessonWeek = (plan: LessonPlan): Omit<LessonWeekGroup, 'plans'> => {
  const weekNumber = Number(plan.week_number);
  if (Number.isInteger(weekNumber) && weekNumber >= 1 && weekNumber <= 52) {
    return { key: `week-${weekNumber}`, label: `Week ${weekNumber}` };
  }

  return { key: 'week-not-set', label: 'Week not set' };
};

const groupLessonPlansByWeek = (plans: LessonPlan[]): LessonWeekGroup[] => {
  const groups = new Map<string, LessonWeekGroup>();
  plans.forEach((plan) => {
    const week = lessonWeek(plan);
    const existing = groups.get(week.key);
    if (existing) {
      existing.plans.push(plan);
    } else {
      groups.set(week.key, { ...week, plans: [plan] });
    }
  });
  return Array.from(groups.values());
};

const defaultCurriculumYear = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const collectImportErrorMessages = (value: any, prefix = ''): string[] => {
  if (value == null) return [];
  if (typeof value === 'string') {
    return [prefix ? `${prefix}: ${value}` : value];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectImportErrorMessages(item, prefix));
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) => {
      const nextPrefix = key === 'non_field_errors'
        ? prefix
        : (prefix ? `${prefix}.${key}` : key);
      return collectImportErrorMessages(item, nextPrefix);
    });
  }
  return [prefix ? `${prefix}: ${String(value)}` : String(value)];
};

const extractImportErrorMessage = (data: any): string => {
  const messages = collectImportErrorMessages(data)
    .map((item) => item.trim())
    .filter(Boolean);
  return messages.slice(0, 3).join(' ');
};

const HookMetric: React.FC<{
  label: string;
  value: React.ReactNode;
  detail: React.ReactNode;
  accentClassName: string;
}> = ({ label, value, detail, accentClassName }) => (
  <Card className="overflow-hidden">
    <div className={`h-1.5 ${accentClassName}`} />
    <CardContent className="space-y-2 pt-4">
      <p className="text-xs uppercase tracking-[0.18em] text-muted">{label}</p>
      <p className="text-3xl font-semibold tracking-tight text-text">{value}</p>
      <p className="text-sm text-muted">{detail}</p>
    </CardContent>
  </Card>
);

export const V2PlanHookDetail: React.FC = () => {
  const { hookId } = useParams();
  const navigate = useNavigate();
  const [plannerParams] = useSearchParams();
  const plannerReturn = plannerParams.get('planner') || '';
  const safePlannerReturn = plannerReturn.startsWith('/v2/teacher/planning?') ? plannerReturn : '';
  const repositoryOpened = useRef(false);
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_plan');
  const [hook, setHook] = useState<Hook | null>(null);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showArchivedPlans, setShowArchivedPlans] = useState(false);
  const [selectedLessonWeek, setSelectedLessonWeek] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [levelSaving, setLevelSaving] = useState<number | null>(null);

  const changeHookLevel = async (plan: LessonPlan, level: string) => {
    setLevelSaving(plan.id); setError(''); setSuccess('');
    try {
      const response = await lessonPlansApi.patch(plan.id, { hook_level: level });
      setLessonPlans((plans) => plans.map((row) => row.id === plan.id ? { ...row, hook_level: response.data.hook_level } : row));
      setSuccess(`Hook level saved for ${plan.title}. Current and historical evidence will use this label.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not save the Hook level. Please try again.');
    } finally { setLevelSaving(null); }
  };

  const hookLevelControl = (plan: LessonPlan) => !plan.assessment_source ? (
    <label className="mt-2 block text-xs text-muted">Hook level
      <select aria-label={`Hook level for ${plan.title}`} className="ml-2 rounded border border-border bg-surface px-2 py-1 text-text"
        value={plan.hook_level || ''} disabled={levelSaving !== null}
        onChange={(event) => void changeHookLevel(plan, event.target.value)}>
        <option value="">Not assigned</option>
        {['define', 'do', 'explain', 'apply'].map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
      </select>
      <span className="mt-1 block">Groups current and historical lesson evidence.</span>
    </label>
  ) : null;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [standards, setStandards] = useState('');
  const [applyCriteria, setApplyCriteria] = useState('');
  const [stageGuidance, setStageGuidance] = useState<Record<string, string>>({});
  const [aiDraftModalOpen, setAiDraftModalOpen] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(45);
  const [hookMode, setHookMode] = useState<'full' | 'micro' | 'continue' | 'practice' | 'reteach'>('full');
  const [guidanceMode, setGuidanceMode] = useState<'strict' | 'flexible'>('strict');
  const [focusNote, setFocusNote] = useState('');
  const [continueFromStage, setContinueFromStage] = useState('why');
  const [draftLoading, setDraftLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [aiDraft, setAiDraft] = useState<any>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importedPlanId, setImportedPlanId] = useState<number | null>(null);
  const [repositoryModalOpen, setRepositoryModalOpen] = useState(false);
  const [repositoryLessons, setRepositoryLessons] = useState<RepositoryLesson[]>([]);
  const [repositoryMeetings, setRepositoryMeetings] = useState<RepositoryMeeting[]>([]);
  const [repositoryProjects, setRepositoryProjects] = useState<RepositoryProject[]>([]);
  const [repositoryProjectKey, setRepositoryProjectKey] = useState('');
  const [repositoryProjectTitle, setRepositoryProjectTitle] = useState('');
  const [repositorySourceLabel, setRepositorySourceLabel] = useState('');
  const [repositorySelectedWeek, setRepositorySelectedWeek] = useState('');
  const [repositorySelectedKeys, setRepositorySelectedKeys] = useState<string[]>([]);
  const [repositoryLoading, setRepositoryLoading] = useState(false);
  const [repositoryImporting, setRepositoryImporting] = useState(false);
  const [repositoryError, setRepositoryError] = useState('');
  const [repositoryMaterialPreview, setRepositoryMaterialPreview] = useState<RepositoryLesson | null>(null);
  const [repositorySyncing, setRepositorySyncing] = useState(false);
  const [copyHookModalOpen, setCopyHookModalOpen] = useState(false);
  const [copyHookTitle, setCopyHookTitle] = useState('');
  const [copyCurriculumYear, setCopyCurriculumYear] = useState(defaultCurriculumYear());
  const [copyHookPlanIds, setCopyHookPlanIds] = useState<number[]>([]);
  const [copyingHook, setCopyingHook] = useState(false);
  const [copyPlanSource, setCopyPlanSource] = useState<LessonPlan | null>(null);
  const [copyPlanTargets, setCopyPlanTargets] = useState<Hook[]>([]);
  const [copyPlanTargetId, setCopyPlanTargetId] = useState('');
  const [copyPlanTitle, setCopyPlanTitle] = useState('');
  const [copyingPlan, setCopyingPlan] = useState(false);

  const resetImportModal = () => {
    setImportError('');
    setImportWarnings([]);
    setImportedPlanId(null);
    setImportFile(null);
    setImportText('');
  };

  const normalizedPlans = useMemo(() => {
    if (!showArchivedPlans) {
      return lessonPlans.filter((plan) => !plan.is_archived);
    }
    return lessonPlans;
  }, [lessonPlans, showArchivedPlans]);
  const repositoryWeeks = useMemo(
    () => repositoryWeekOptions(repositoryLessons),
    [repositoryLessons],
  );
  const visibleRepositoryLessons = useMemo(
    () => repositoryWeeks.find((week) => week.key === repositorySelectedWeek)?.lessons || [],
    [repositorySelectedWeek, repositoryWeeks],
  );
  const lessonWeekGroups = useMemo(
    () => groupLessonPlansByWeek(normalizedPlans),
    [normalizedPlans],
  );
  const activeLessonWeek = lessonWeekGroups.some((group) => group.key === selectedLessonWeek)
    ? selectedLessonWeek
    : lessonWeekGroups[0]?.key;
  const visibleWeekPlans = lessonWeekGroups.find((group) => group.key === activeLessonWeek)?.plans || [];
  const draftStepsSorted = useMemo(() => {
    const steps = Array.isArray(aiDraft?.steps) ? aiDraft.steps : [];
    return [...steps].sort((a, b) => {
      const aOrder = Number(a?.order || 0);
      const bOrder = Number(b?.order || 0);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a?.title || '').localeCompare(String(b?.title || ''));
    });
  }, [aiDraft]);
  const groupedDraftSteps = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    draftStepsSorted.forEach((step) => {
      const stage = String(step?.stage_tag || '').toLowerCase();
      if (!grouped[stage]) grouped[stage] = [];
      grouped[stage].push(step);
    });
    return grouped;
  }, [draftStepsSorted]);

  const load = async () => {
    if (!hookId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [hookRes, plansRes] = await Promise.all([
        hooksV2Api.get(Number(hookId)),
        lessonPlansApi.list(),
      ]);
      const hookData: Hook = hookRes.data;
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : plansRes.data?.results || [];
      setHook(hookData);
      setLessonPlans(plansData.filter((plan: LessonPlan) => plan.hook === hookData.id && !(plan as any).curriculum_delivery));
      setTitle(hookData.title || '');
      setDescription(hookData.description || '');
      setStandards(hookData.hook_settings?.standard_code || '');
      setApplyCriteria(hookData.apply_criteria?.mastery_criteria || '');
      setStageGuidance({
        why: hookData.stage_guidance?.why || '',
        define: hookData.stage_guidance?.define || '',
        do: hookData.stage_guidance?.do || '',
        explain: hookData.stage_guidance?.explain || '',
        apply: hookData.stage_guidance?.apply || '',
        reflect: hookData.stage_guidance?.reflect || '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [hookId]);

  useTeacherUnsavedChanges(!!hook && !loading && !saving && (
    title !== (hook.title || '') || description !== (hook.description || '') ||
    standards !== (hook.hook_settings?.standard_code || '') || applyCriteria !== (hook.apply_criteria?.mastery_criteria || '') ||
    Object.keys(stageGuidance).some(key => stageGuidance[key] !== (hook.stage_guidance?.[key] || ''))
  ));
  const handleSave = async () => {
    if (!hook) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const nextHookSettings = {
        ...(hook.hook_settings || {}),
        standard_code: standards,
      };
      await hooksV2Api.update(hook.id, {
        title,
        description,
        stage_guidance: stageGuidance,
        apply_criteria: { mastery_criteria: applyCriteria },
        hook_settings: nextHookSettings,
        is_archived: hook.is_archived || false,
      });
      await load();
      setSuccess('Hook updated.');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateHook = async () => {
    if (!hook) return;
    setCopyHookTitle(`${hook.title} — ${defaultCurriculumYear()}`);
    setCopyCurriculumYear(defaultCurriculumYear());
    setCopyHookPlanIds(lessonPlans.filter((plan) => !plan.is_archived).map((plan) => plan.id));
    setCopyHookModalOpen(true);
  };

  const handleCopyHookWithLessons = async () => {
    if (!hook || !copyHookTitle.trim()) return;
    setCopyingHook(true);
    setError('');
    try {
      const response = await hooksV2Api.copyWithLessons(hook.id, {
        title: copyHookTitle.trim(),
        curriculum_year: copyCurriculumYear.trim(),
        lesson_plan_ids: copyHookPlanIds,
      });
      const targetHookId = Number(response.data?.hook?.id);
      setCopyHookModalOpen(false);
      if (targetHookId) {
        navigate(`/v2/teacher/plan/hooks/${targetHookId}`);
      } else {
        navigate('/v2/teacher/plan');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to copy this hook.');
    } finally {
      setCopyingHook(false);
    }
  };

  const handleArchiveHook = async () => {
    if (!hook) return;
    setError('');
    setSuccess('');
    try {
      await hooksV2Api.patch(hook.id, { is_archived: true });
      await load();
      setSuccess('Hook archived.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.detail || 'Unable to archive this hook.');
    }
  };

  const handleRestoreHook = async () => {
    if (!hook) return;
    setError('');
    setSuccess('');
    await hooksV2Api.restore(hook.id);
    await load();
    setSuccess('Hook restored and available for reuse.');
  };

  const handleDeleteHook = async () => {
    if (!hook) return;
    setError('');
    setSuccess('');
    try {
      await hooksV2Api.delete(hook.id);
      navigate('/v2/teacher/plan');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to delete hook. Archive instead.');
    }
  };

  const handleDuplicatePlan = async (plan: LessonPlan) => {
    if (!hook) return;
    setError('');
    setSuccess('');
    await lessonPlansApi.copyToHook(plan.id, hook.id, `${plan.title} (Copy)`);
    await load();
    setSuccess('Lesson plan and all steps duplicated.');
  };

  const openCopyPlanToHook = async (plan: LessonPlan) => {
    setError('');
    const response = await hooksV2Api.list();
    const rows: Hook[] = Array.isArray(response.data) ? response.data : response.data?.results || [];
    const targets = rows.filter((row) => !row.is_archived && row.id !== hook?.id);
    setCopyPlanTargets(targets);
    setCopyPlanTargetId(targets[0]?.id ? String(targets[0].id) : '');
    setCopyPlanTitle(plan.title);
    setCopyPlanSource(plan);
  };

  const handleCopyPlanToHook = async () => {
    if (!copyPlanSource || !copyPlanTargetId) return;
    setCopyingPlan(true);
    setError('');
    try {
      await lessonPlansApi.copyToHook(
        copyPlanSource.id,
        Number(copyPlanTargetId),
        copyPlanTitle.trim() || undefined,
      );
      setCopyPlanSource(null);
      setSuccess('Lesson plan and all steps copied to the selected hook.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to copy this lesson plan.');
    } finally {
      setCopyingPlan(false);
    }
  };

  const handleArchivePlan = async (plan: LessonPlan) => {
    setError('');
    setSuccess('');
    await lessonPlansApi.patch(plan.id, { is_archived: true });
    await load();
    setSuccess('Lesson plan archived.');
  };

  const handleGenerateDraft = async () => {
    if (!hook) return;
    setDraftError('');
    setDraftLoading(true);
    try {
      const payload: any = {
        duration_minutes: Number(durationMinutes),
        hook_mode: hookMode,
        guidance_mode: guidanceMode,
      };
      if (focusNote.trim()) payload.focus_note = focusNote.trim();
      if (hookMode === 'continue') payload.continue_from_stage = continueFromStage;
      const response = await hooksV2Api.aiDraftLesson(hook.id, payload);
      const draft = response?.data?.draft;
      if (!draft || !Array.isArray(draft.steps)) {
        setDraftError('AI draft failed: invalid draft format.');
        return;
      }
      setAiDraft(draft);
    } catch (err: any) {
      setDraftError(err?.response?.data?.error || 'Failed to generate AI draft.');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleCreateFromDraft = async () => {
    if (!hook || !aiDraft) return;
    setDraftError('');
    setCreateLoading(true);
    try {
      const createdPlanRes = await lessonPlansApi.create({
        hook: hook.id,
        title: String(aiDraft.lesson_title || 'AI Draft Lesson').trim() || 'AI Draft Lesson',
        description: `${String(aiDraft.objective || '').trim()}\n\n${String(aiDraft.big_idea || '').trim()}`.trim(),
      });
      const createdPlan = createdPlanRes?.data || {};
      const planId = Number(createdPlan.id);
      if (!planId) {
        setDraftError('Failed to create lesson plan from draft.');
        return;
      }
      for (const step of draftStepsSorted) {
        const promptMd = String(step?.content?.prompt_md || '').trim();
        const contentPayload: any = {
          ...(step?.content && typeof step.content === 'object' ? step.content : {}),
          prompt_md: promptMd,
        };
        if (step?.suggested_signal_config && typeof step.suggested_signal_config === 'object') {
          contentPayload.suggested_signal_config = step.suggested_signal_config;
        }
        const expectedProficiencyRaw =
          typeof step?.expected_proficiency === 'string'
            ? step.expected_proficiency
            : (typeof step?.expectedProficiency === 'string' ? step.expectedProficiency : '');
        const expectedProficiency = expectedProficiencyRaw.trim();
        if (expectedProficiency) {
          contentPayload.expected_proficiency = expectedProficiency;
        }
        const expectedMasteryRaw =
          typeof step?.expected_mastery === 'string'
            ? step.expected_mastery
            : (typeof step?.expectedMastery === 'string' ? step.expectedMastery : '');
        const expectedMastery = expectedMasteryRaw.trim();
        if (expectedMastery) {
          contentPayload.expected_mastery = expectedMastery;
        }
        const countsAsEvidence =
          typeof step?.counts_as_evidence === 'boolean' ? step.counts_as_evidence : undefined;
        const evidenceConfig =
          step?.evidence_config && typeof step.evidence_config === 'object'
            ? step.evidence_config
            : undefined;
        await lessonPlansApi.addStep(planId, {
          title: String(step?.title || '').trim(),
          order: Number(step?.order || 0),
          stage_tag: String(step?.stage_tag || '').toLowerCase(),
          step_kind: String(step?.step_kind || '').trim(),
          content: contentPayload,
          ...(countsAsEvidence !== undefined ? { counts_as_evidence: countsAsEvidence } : {}),
          ...(evidenceConfig ? { evidence_config: evidenceConfig } : {}),
        });
      }
      navigate(`/v2/teacher/lesson-plan?hookId=${hook.id}&planId=${planId}`);
    } catch (err: any) {
      setDraftError(err?.response?.data?.error || 'Failed to create lesson plan from draft.');
    } finally {
      setCreateLoading(false);
    }
  };

  const warningMessages = (warnings: any): string[] => {
    if (!Array.isArray(warnings)) return [];
    return warnings
      .map((warning) => {
        if (typeof warning === 'string') return warning;
        if (warning && typeof warning === 'object' && typeof warning.message === 'string') {
          return warning.message;
        }
        return '';
      })
      .filter(Boolean);
  };

  const handleImportBundle = async () => {
    if (!hook) return;
    setImportError('');
    setImportWarnings([]);
    setImportedPlanId(null);
    setImportLoading(true);
    try {
      let response;
      let bundle;
      if (importFile) {
        const rawText = await importFile.text();
        try {
          bundle = JSON.parse(rawText);
        } catch {
          setImportError('Uploaded file must contain valid JSON.');
          return;
        }
      } else {
        const text = importText.trim();
        if (!text) {
          setImportError('Upload a .json file or paste bundle JSON.');
          return;
        }
        try {
          bundle = JSON.parse(text);
        } catch {
          setImportError('Invalid JSON in text area.');
          return;
        }
      }
      bundle.plan = { ...(bundle.plan || {}), hook: hook.id };
      response = await lessonPlansApi.importBundle({ bundle });

      const planId = Number(response?.data?.plan_id || 0);
      if (!planId) {
        setImportError('Import succeeded but no plan_id was returned.');
        return;
      }
      const warnings = warningMessages(response?.data?.warnings);
      if (warnings.length) {
        setImportWarnings(warnings);
        setImportedPlanId(planId);
        return;
      }
      setImportModalOpen(false);
      setImportFile(null);
      setImportText('');
      navigate(`/v2/teacher/lesson-plan/${planId}/edit`);
    } catch (err: any) {
      const data = err?.response?.data;
      const fileError = Array.isArray(data?.file) ? data.file.join(' ') : '';
      const detailError = typeof data?.detail === 'string' ? data.detail : '';
      const structuredError = extractImportErrorMessage(data);
      setImportError(fileError || detailError || structuredError || data?.error || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  const loadRepositoryCatalog = async (projectKey?: string) => {
    if (!hook) return;
    setRepositoryLoading(true);
    setRepositoryError('');
    try {
      const response = await lessonPlansApi.repositoryCatalog(hook.id, projectKey);
      const rows: RepositoryLesson[] = (Array.isArray(response.data?.results) ? response.data.results : []).map((row: any) => ({
        ...row,
        week_number: Number.isInteger(Number(row?.week_number)) && Number(row?.week_number) >= 1
          ? Number(row.week_number)
          : null,
        source_hash: String(row?.source_hash || ''),
        resource_summary: row?.resource_summary || {
          incoming_count: 0,
          printable_count: 0,
          teacher_only_count: 0,
          student_accessible_count: 0,
          source_types: [],
          has_changes: false,
          added: [],
          removed: [],
          changed: [],
        },
      }));
      const projects: RepositoryProject[] = Array.isArray(response.data?.projects) ? response.data.projects : [];
      setRepositoryLessons(rows);
      setRepositoryMeetings(Array.isArray(response.data?.meetings) ? response.data.meetings : []);
      setRepositoryProjects(projects);
      setRepositoryProjectKey(String(response.data?.project_key || ''));
      setRepositoryProjectTitle(String(response.data?.project_title || ''));
      const repository = String(response.data?.source?.repository || '').trim();
      const branch = String(response.data?.source?.branch || '').trim();
      setRepositorySourceLabel(repository ? `${repository}${branch ? ` · ${branch}` : ''}` : 'Configured curriculum repository');
      const weeks = repositoryWeekOptions(rows);
      const selectedWeek = weeks[0]?.key || '';
      setRepositorySelectedWeek(selectedWeek);
      setRepositorySelectedKeys(
        safePlannerReturn ? [] : (weeks[0]?.lessons || []).filter((row) => row.can_add).map((row) => row.key),
      );
      if (rows.length === 0 && response.data?.detail) {
        setRepositoryError(String(response.data.detail));
      }
    } catch (err: any) {
      setRepositoryError(err?.response?.data?.detail || 'Unable to load repository lessons.');
    } finally {
      setRepositoryLoading(false);
    }
  };

  const openRepositoryCatalog = async () => {
    setRepositoryModalOpen(true);
    await loadRepositoryCatalog();
  };

  useEffect(() => {
    if (hook && plannerParams.get('repository') === '1' && !repositoryOpened.current) {
      repositoryOpened.current = true;
      void openRepositoryCatalog();
    }
    // Open the requested picker once after the Hook has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hook, plannerParams]);

  const handleImportRepositoryLessons = async () => {
    if (!hook || !repositoryProjectKey || repositorySelectedKeys.length === 0) return;
    setRepositoryImporting(true);
    setRepositoryError('');
    try {
      let restoredCount = 0;
      for (const sourceKey of repositorySelectedKeys) {
        const response = await lessonPlansApi.importRepositoryLesson(hook.id, repositoryProjectKey, sourceKey);
        if (response.data?.restored) restoredCount += 1;
      }
      const importedCount = repositorySelectedKeys.length;
      setRepositoryModalOpen(false);
      setRepositorySelectedKeys([]);
      await load();
      const restoredLabel = restoredCount > 0
        ? ` ${restoredCount} archived lesson${restoredCount === 1 ? '' : 's'} restored.`
        : '';
      setSuccess(`${importedCount} repository lesson${importedCount === 1 ? '' : 's'} added to this hook.${restoredLabel}`);
    } catch (err: any) {
      const data = err?.response?.data;
      setRepositoryError(extractImportErrorMessage(data) || data?.detail || 'Unable to add repository lessons.');
    } finally {
      setRepositoryImporting(false);
    }
  };

  const enableImportedMeeting = async (meeting: RepositoryMeeting) => {
    if (!hook || !meeting.items[0]) return;
    setRepositoryImporting(true);
    setRepositoryError('');
    try {
      await lessonPlansApi.importRepositoryLesson(hook.id, meeting.project_key, meeting.items[0].source_key);
      await loadRepositoryCatalog(repositoryProjectKey);
      setSuccess(`${meeting.title} is available in daily planning.`);
    } catch (err: any) {
      setRepositoryError(err?.response?.data?.detail || 'Unable to enable this meeting in daily planning.');
    } finally {
      setRepositoryImporting(false);
    }
  };

  const handleUpdateRepositoryLesson = async () => {
    if (!hook || !repositoryMaterialPreview?.lesson_plan_id) return;
    setRepositorySyncing(true);
    setRepositoryError('');
    try {
      await lessonPlansApi.updateRepositoryLesson(
        hook.id,
        repositoryMaterialPreview.project_key,
        repositoryMaterialPreview.key,
        repositoryMaterialPreview.source_hash,
      );
      const lessonTitle = repositoryMaterialPreview.title;
      setRepositoryMaterialPreview(null);
      await loadRepositoryCatalog(repositoryProjectKey);
      await load();
      setSuccess(`${lessonTitle} updated from the curriculum repository. The previous revision was archived.`);
    } catch (err: any) {
      setRepositoryError(err?.response?.data?.detail || 'Unable to update the repository lesson.');
      setRepositoryMaterialPreview(null);
    } finally {
      setRepositorySyncing(false);
    }
  };

  if (loading || !hook) {
    return (
      redesignEnabled ? (
        <AppShell role="teacher" navItems={teacherV2NavItems('plan')} title="Plan">
          <div className="mx-auto max-w-6xl">
            <p className="text-gray-500">Loading hook...</p>
          </div>
        </AppShell>
      ) : (
        <>
          <TeacherNav activeTab="plan" />
          <div className="max-w-6xl mx-auto p-6">
            <p className="text-gray-500">Loading hook...</p>
          </div>
        </>
      )
    );
  }

  const canDelete = !(hook.has_evidence || hook.has_runs);
  const assignedSectionsCount = Array.isArray(hook.assignments) ? hook.assignments.length : 0;
  const stageGuidanceCount = STAGES.filter((stage) => String(stageGuidance[stage] || '').trim()).length;
  const activeLessonPlanCount = normalizedPlans.filter((plan) => !plan.is_archived).length;
  const pageContent = (
    <div className={redesignEnabled ? 'mx-auto max-w-6xl space-y-6' : 'max-w-6xl mx-auto p-6 space-y-6'}>
      <div>
        {redesignEnabled ? (
          <PageHeader
            eyebrow="Hook Workspace"
            title={hook.title}
            subtitle="Shape the hook definition, draft lessons, and manage section availability from one place."
            actions={(
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/v2/teacher/plan')}
                >
                  Back to Plan
                </Button>
                <Badge variant={hook.is_archived ? 'muted' : 'default'}>
                  {hook.is_archived ? 'Archived' : 'Active'}
                </Badge>
              </>
            )}
          />
        ) : (
          <>
            <button
              className="text-sm text-indigo-600"
              onClick={() => navigate('/v2/teacher/plan')}
            >
              ← Back to Plan
            </button>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{hook.title}</h1>
                <p className="text-sm text-gray-500">Hook detail and lesson plan management</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                  onClick={handleDuplicateHook}
                >
                  Copy Hook & Lessons
                </button>
                {hook.is_archived ? (
                  <button
                    className="px-3 py-2 border border-indigo-300 text-indigo-700 rounded text-sm"
                    onClick={handleRestoreHook}
                  >
                    Restore Hook
                  </button>
                ) : (
                  <button
                    className="px-3 py-2 border border-amber-400 text-amber-700 rounded text-sm"
                    onClick={handleArchiveHook}
                  >
                    Archive Hook
                  </button>
                )}
                <button
                  className={`px-3 py-2 rounded text-sm ${canDelete ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}
                  onClick={handleDeleteHook}
                  disabled={!canDelete}
                  title={canDelete ? 'Delete hook' : 'Hook has evidence or runs; archive only'}
                >
                  Delete Hook
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {redesignEnabled && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <HookMetric
            label="Lesson Plans"
            value={activeLessonPlanCount}
            detail={`${normalizedPlans.length - activeLessonPlanCount > 0 ? `${normalizedPlans.length - activeLessonPlanCount} archived hidden/visible by toggle.` : 'All visible plans are active.'}`}
            accentClassName="bg-brand"
          />
          <HookMetric
            label="Assigned Sections"
            value={assignedSectionsCount}
            detail={assignedSectionsCount === 0 ? 'Not yet assigned to any sections.' : 'Sections currently receiving this hook.'}
            accentClassName="bg-success"
          />
          <HookMetric
            label="Stage Guidance"
            value={stageGuidanceCount}
            detail={`${STAGES.length - stageGuidanceCount} stage${STAGES.length - stageGuidanceCount === 1 ? '' : 's'} still need guidance.`}
            accentClassName="bg-warning"
          />
          <HookMetric
            label="Lifecycle"
            value={hook.is_archived ? 'Archived' : canDelete ? 'Editable' : 'Protected'}
            detail={canDelete ? 'No evidence or runs are blocking deletion.' : 'Archive-only because evidence or runs already exist.'}
            accentClassName="bg-slate-700"
          />
        </div>
      )}

      {redesignEnabled ? (
        <SectionCard
          title="Hook Definition"
          meta="Set the standards anchor, mastery criteria, and stage-by-stage teaching guidance."
          actions={(
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDuplicateHook}
              >
                Copy Hook & Lessons
              </Button>
              {hook.is_archived ? (
                <Button variant="secondary" size="sm" onClick={handleRestoreHook}>
                  Restore Hook
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleArchiveHook}
                >
                  Archive Hook
                </Button>
              )}
              <Button
                variant={canDelete ? 'destructive' : 'secondary'}
                size="sm"
                onClick={handleDeleteHook}
                disabled={!canDelete}
                title={canDelete ? 'Delete hook' : 'Hook has evidence or runs; archive only'}
              >
                Delete Hook
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Hook'}
              </Button>
            </div>
          )}
          contentClassName="space-y-5"
        >
          {error && (
            <div className="rounded-lg border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">
              {success}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text">Hook Title</label>
              <Input
                className="mt-2"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text">Standards / Course Pathway</label>
              <Input
                className="mt-2"
                value={standards}
                onChange={(e) => setStandards(e.target.value)}
                placeholder="e.g., IT Essentials · Networking"
              />
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text">WHY / Context</label>
                <Textarea
                  className="mt-2"
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text">APPLY Mastery Criteria</label>
                <Textarea
                  className="mt-2"
                  rows={5}
                  value={applyCriteria}
                  onChange={(e) => setApplyCriteria(e.target.value)}
                  placeholder="Describe what counts as mastery at Apply"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-surface-alt/50 p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-muted">Readiness Snapshot</p>
              <div className="mt-3 space-y-3">
                <div className="rounded-lg border border-border bg-surface px-4 py-3">
                  <p className="text-sm font-medium text-text">Section coverage</p>
                  <p className="mt-1 text-sm text-muted">
                    {assignedSectionsCount === 0
                      ? 'No active section assignments yet.'
                      : `${assignedSectionsCount} section${assignedSectionsCount === 1 ? '' : 's'} currently use this hook.`}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-surface px-4 py-3">
                  <p className="text-sm font-medium text-text">Lesson plan inventory</p>
                  <p className="mt-1 text-sm text-muted">
                    {activeLessonPlanCount} active plan{activeLessonPlanCount === 1 ? '' : 's'} ready for launch.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-surface px-4 py-3">
                  <p className="text-sm font-medium text-text">Deletion guard</p>
                  <p className="mt-1 text-sm text-muted">
                    {canDelete ? 'This hook can be deleted cleanly if you no longer need it.' : 'Delete is blocked because evidence or lesson runs already exist.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-text">Stage Guidance</h3>
                <p className="mt-1 text-sm text-muted">Clarify what students should experience in each phase of the hook.</p>
              </div>
              <Badge variant="muted">{stageGuidanceCount} / {STAGES.length} filled</Badge>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {STAGES.map((stage) => (
                <div key={stage} className="rounded-xl border border-border bg-surface-alt/40 p-4">
                  <label className="block text-sm font-medium text-text">
                    {stage.toUpperCase()} Guidance
                  </label>
                  <Textarea
                    className="mt-2"
                    rows={4}
                    value={stageGuidance[stage] || ''}
                    onChange={(e) =>
                      setStageGuidance((prev) => ({
                        ...prev,
                        [stage]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      ) : (
      <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded">
            {success}
          </div>
        )}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Hook Definition</h2>
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Hook'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Hook Title</label>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Standards / Course Pathway</label>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={standards}
              onChange={(e) => setStandards(e.target.value)}
              placeholder="e.g., IT Essentials · Networking"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">WHY / Context</label>
          <textarea
            className="mt-1 w-full border rounded px-3 py-2"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">APPLY Mastery Criteria</label>
          <textarea
            className="mt-1 w-full border rounded px-3 py-2"
            rows={3}
            value={applyCriteria}
            onChange={(e) => setApplyCriteria(e.target.value)}
            placeholder="Describe what counts as mastery at Apply"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {STAGES.map((stage) => (
            <div key={stage}>
              <label className="block text-sm font-medium text-gray-700">
                {stage.toUpperCase()} Guidance
              </label>
              <textarea
                className="mt-1 w-full border rounded px-3 py-2"
                rows={3}
                value={stageGuidance[stage] || ''}
                onChange={(e) =>
                  setStageGuidance((prev) => ({
                    ...prev,
                    [stage]: e.target.value,
                  }))
                }
              />
            </div>
          ))}
        </div>
      </section>
      )}

      {redesignEnabled ? (
        <SectionCard
          title="Lesson Plans"
          meta="Launch new plans from this hook, review older drafts, or import curriculum bundles."
          actions={(
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowArchivedPlans((prev) => !prev)}
              >
                {showArchivedPlans ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/v2/teacher/lesson-plan?hookId=${hook.id}`)}
              >
                New Lesson Plan
              </Button>
              <Button variant="secondary" size="sm" onClick={openRepositoryCatalog}>
                <BookOpenIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                Add from Repository
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  resetImportModal();
                  setImportModalOpen(true);
                }}
              >
                Import Plan
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setAiDraftModalOpen(true);
                  setAiDraft(null);
                  setDraftError('');
                }}
              >
                Draft Lesson with AI
              </Button>
            </div>
          )}
          contentClassName="space-y-4"
        >
          {normalizedPlans.length === 0 ? (
            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
              No lesson plans yet.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2" aria-label="Lesson weeks">
                {lessonWeekGroups.map((group) => (
                  <Button
                    key={group.key}
                    variant={activeLessonWeek === group.key ? 'primary' : 'secondary'}
                    size="sm"
                    aria-pressed={activeLessonWeek === group.key}
                    onClick={() => setSelectedLessonWeek(group.key)}
                  >
                    {group.label}
                    <span
                      className="ml-2 rounded-full bg-white/20 px-1.5 text-xs"
                      aria-label={`${group.plans.length} lessons`}
                    >
                      {group.plans.length}
                    </span>
                  </Button>
                ))}
              </div>
              <div className="grid gap-3">
                {visibleWeekPlans.map((plan) => (
                  <div key={plan.id} className="rounded-xl border border-border bg-surface px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-text">{plan.title}</p>
                        {hookLevelControl(plan)}
                        {plan.description && (
                          <p className="mt-1 text-sm text-muted max-w-2xl">{plan.description}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {plan.is_archived ? <Badge variant="muted">Archived</Badge> : <Badge variant="default">Active</Badge>}
                        {plan.assessment_source && <Badge variant="warning">Assessment v{plan.assessment_source.version_number}</Badge>}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/v2/teacher/lesson-plan/${plan.id}/edit`)}
                        >
                          {plan.assessment_source ? 'View delivery' : 'Edit'}
                        </Button>
                        {plan.assessment_source ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/v2/teacher/assessments?assessmentId=${plan.assessment_source?.assessment_id}`)}
                          >
                            Edit assessment
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDuplicatePlan(plan)}
                            >
                              Duplicate
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCopyPlanToHook(plan)}
                            >
                              Copy to Hook
                            </Button>
                          </>
                        )}
                        {!plan.is_archived && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleArchivePlan(plan)}
                          >
                            Archive
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
      ) : (
      <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Lesson Plans</h2>
            <p className="text-sm text-gray-500">Lesson plans are created inside this hook.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-2 border border-gray-300 rounded text-sm"
              onClick={() => setShowArchivedPlans((prev) => !prev)}
            >
              {showArchivedPlans ? 'Hide Archived' : 'Show Archived'}
            </button>
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
              onClick={() => navigate(`/v2/teacher/lesson-plan?hookId=${hook.id}`)}
            >
              New Lesson Plan
            </button>
            <button
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded text-sm"
              onClick={openRepositoryCatalog}
            >
              <BookOpenIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Add from Repository
            </button>
            <button
              className="px-4 py-2 border border-gray-300 rounded text-sm"
              onClick={() => {
                resetImportModal();
                setImportModalOpen(true);
              }}
            >
              Import Plan
            </button>
            <button
              className="px-4 py-2 border border-indigo-300 text-indigo-700 rounded text-sm"
              onClick={() => {
                setAiDraftModalOpen(true);
                setAiDraft(null);
                setDraftError('');
              }}
            >
              Draft Lesson with AI
            </button>
          </div>
        </div>

        {normalizedPlans.length === 0 ? (
          <p className="text-sm text-gray-500">No lesson plans yet.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" aria-label="Lesson weeks">
              {lessonWeekGroups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  aria-pressed={activeLessonWeek === group.key}
                  className={`rounded border px-3 py-2 text-sm ${
                    activeLessonWeek === group.key
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedLessonWeek(group.key)}
                >
                  {group.label}
                  <span className="ml-2 opacity-75" aria-label={`${group.plans.length} lessons`}>
                    {group.plans.length}
                  </span>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {visibleWeekPlans.map((plan) => (
                <div key={plan.id} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{plan.title}</p>
                    {hookLevelControl(plan)}
                    {plan.description && (
                      <p className="text-xs text-gray-500 max-w-xl truncate">{plan.description}</p>
                    )}
                    {plan.is_archived && (
                      <span className="text-xs text-gray-400">Archived</span>
                    )}
                    {plan.assessment_source && (
                      <span className="text-xs font-semibold text-indigo-700">Assessment delivery · v{plan.assessment_source.version_number}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => navigate(`/v2/teacher/lesson-plan/${plan.id}/edit`)}
                    >
                      {plan.assessment_source ? 'View delivery' : 'Edit'}
                    </button>
                    {plan.assessment_source ? (
                      <button
                        className="text-xs text-indigo-600 hover:underline"
                        onClick={() => navigate(`/v2/teacher/assessments?assessmentId=${plan.assessment_source?.assessment_id}`)}
                      >
                        Edit assessment
                      </button>
                    ) : (
                      <>
                        <button
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={() => handleDuplicatePlan(plan)}
                        >
                          Duplicate
                        </button>
                        <button
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={() => openCopyPlanToHook(plan)}
                        >
                          Copy to Hook
                        </button>
                      </>
                    )}
                    {plan.is_archived ? (
                      <span className="text-xs text-gray-400">Archived</span>
                    ) : (
                      <button
                        className="text-xs text-amber-600 hover:underline"
                        onClick={() => handleArchivePlan(plan)}
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </section>
      )}

      {redesignEnabled ? (
        <SectionCard
          title="Usage"
          meta="Track where this hook is active across sections."
        >
          {hook.assignments?.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {hook.assignments.map((assignment) => (
                <div key={assignment.id} className="rounded-xl border border-border bg-surface px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-text">{assignment.section_name || `Section ${assignment.section}`}</span>
                    <Badge variant={assignment.is_active ? 'success' : 'muted'}>
                      {assignment.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
              Not assigned to any sections yet.
            </div>
          )}
        </SectionCard>
      ) : (
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-900">Usage</h2>
        <p className="text-sm text-gray-500">Sections this hook is assigned to.</p>
        {hook.assignments?.length ? (
          <div className="mt-3 space-y-2">
            {hook.assignments.map((assignment) => (
              <div key={assignment.id} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                <span className="text-sm text-gray-900">{assignment.section_name || `Section ${assignment.section}`}</span>
                <span className="text-xs text-gray-500">{assignment.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-gray-500">Not assigned to any sections yet.</p>
        )}
      </section>
      )}
    </div>
  );

  return (
    <>
      {!redesignEnabled && <TeacherNav activeTab="plan" />}
      {safePlannerReturn && <div className="mx-auto max-w-6xl p-4"><Link className="text-brand underline" to={safePlannerReturn}>← Return to unit planner after importing</Link></div>}
      {redesignEnabled ? (
        <AppShell role="teacher" navItems={teacherV2NavItems('plan')} title="Plan">
          {pageContent}
        </AppShell>
      ) : pageContent}

      <Dialog
        open={copyHookModalOpen}
        onClose={() => {
          if (!copyingHook) setCopyHookModalOpen(false);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Copy Hook &amp; Lessons</Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                Create a fresh curriculum copy. Student assignments, submissions, and history stay with the original.
              </p>
            </div>
            <div className="max-h-[75vh] space-y-5 overflow-y-auto p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">New hook title</label>
                  <input
                    className="mt-2 w-full rounded border px-3 py-2"
                    value={copyHookTitle}
                    onChange={(event) => setCopyHookTitle(event.target.value)}
                    disabled={copyingHook}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Curriculum year</label>
                  <input
                    className="mt-2 w-full rounded border px-3 py-2"
                    value={copyCurriculumYear}
                    onChange={(event) => setCopyCurriculumYear(event.target.value)}
                    placeholder="2026-27"
                    disabled={copyingHook}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Lessons to include</p>
                    <p className="text-xs text-gray-500">Archived lessons are available but are not selected automatically.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs font-medium text-indigo-700"
                      onClick={() => setCopyHookPlanIds(lessonPlans.map((plan) => plan.id))}
                      disabled={copyingHook}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs font-medium text-gray-600"
                      onClick={() => setCopyHookPlanIds([])}
                      disabled={copyingHook}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="mt-3 max-h-64 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                  {lessonPlans.length ? lessonPlans.map((plan) => (
                    <label key={plan.id} className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-2 hover:bg-gray-50">
                      <span className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={copyHookPlanIds.includes(plan.id)}
                          onChange={(event) => {
                            setCopyHookPlanIds((current) => (
                              event.target.checked
                                ? [...current, plan.id]
                                : current.filter((id) => id !== plan.id)
                            ));
                          }}
                          disabled={copyingHook}
                        />
                        <span className="text-sm text-gray-900">{plan.title}</span>
                      </span>
                      <span className={`text-xs ${plan.is_archived ? 'text-amber-700' : 'text-green-700'}`}>
                        {plan.is_archived ? 'Archived' : 'Active'}
                      </span>
                    </label>
                  )) : (
                    <p className="py-3 text-center text-sm text-gray-500">This hook has no lessons yet.</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  className="rounded border border-gray-300 px-4 py-2 text-sm"
                  onClick={() => setCopyHookModalOpen(false)}
                  disabled={copyingHook}
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                  onClick={handleCopyHookWithLessons}
                  disabled={copyingHook || !copyHookTitle.trim()}
                >
                  {copyingHook ? 'Copying...' : `Copy Hook${copyHookPlanIds.length ? ` + ${copyHookPlanIds.length} Lessons` : ''}`}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(copyPlanSource)}
        onClose={() => {
          if (!copyingPlan) setCopyPlanSource(null);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Copy Lesson to Hook</Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">The lesson and all of its steps will be copied without student history.</p>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="block text-sm font-medium text-gray-700">Lesson title</label>
                <input
                  className="mt-2 w-full rounded border px-3 py-2"
                  value={copyPlanTitle}
                  onChange={(event) => setCopyPlanTitle(event.target.value)}
                  disabled={copyingPlan}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Destination hook</label>
                {copyPlanTargets.length ? (
                  <select
                    className="mt-2 w-full rounded border px-3 py-2"
                    value={copyPlanTargetId}
                    onChange={(event) => setCopyPlanTargetId(event.target.value)}
                    disabled={copyingPlan}
                  >
                    {copyPlanTargets.map((target) => (
                      <option key={target.id} value={target.id}>{target.title}</option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    No other active hooks are available. Restore or create a destination hook first.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  className="rounded border border-gray-300 px-4 py-2 text-sm"
                  onClick={() => setCopyPlanSource(null)}
                  disabled={copyingPlan}
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                  onClick={handleCopyPlanToHook}
                  disabled={copyingPlan || !copyPlanTargetId}
                >
                  {copyingPlan ? 'Copying...' : 'Copy Lesson'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={aiDraftModalOpen} onClose={() => setAiDraftModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-4xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Draft Lesson with AI</Dialog.Title>
              <p className="text-sm text-gray-500 mt-1">Generate a lesson draft from this hook, preview it, then create a plan.</p>
            </div>

            <div className="p-5 space-y-4">
              {draftError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {draftError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration (minutes)</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[20, 30, 45, 50, 90].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className={`px-2.5 py-1 rounded border text-xs ${durationMinutes === preset ? 'border-indigo-500 text-indigo-700 bg-indigo-50' : 'border-gray-300 text-gray-700'}`}
                        onClick={() => setDurationMinutes(preset)}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="mt-2 w-full border rounded px-3 py-2"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(Number(e.target.value || 45))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Hook mode</label>
                  <select
                    className="mt-2 w-full border rounded px-3 py-2"
                    value={hookMode}
                    onChange={(e) => setHookMode(e.target.value as any)}
                  >
                    <option value="full">full</option>
                    <option value="micro">micro</option>
                    <option value="continue">continue</option>
                    <option value="practice">practice</option>
                    <option value="reteach">reteach</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Guidance mode</label>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className={`px-3 py-2 rounded border text-sm ${guidanceMode === 'strict' ? 'border-indigo-500 text-indigo-700 bg-indigo-50' : 'border-gray-300 text-gray-700'}`}
                    onClick={() => setGuidanceMode('strict')}
                  >
                    strict
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-2 rounded border text-sm ${guidanceMode === 'flexible' ? 'border-indigo-500 text-indigo-700 bg-indigo-50' : 'border-gray-300 text-gray-700'}`}
                    onClick={() => setGuidanceMode('flexible')}
                  >
                    flexible
                  </button>
                </div>
              </div>

              {hookMode === 'continue' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Continue from stage</label>
                  <select
                    className="mt-2 w-full border rounded px-3 py-2"
                    value={continueFromStage}
                    onChange={(e) => setContinueFromStage(e.target.value)}
                  >
                    {STAGES.map((stage) => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Focus note (optional)</label>
                <textarea
                  className="mt-2 w-full border rounded px-3 py-2"
                  rows={3}
                  value={focusNote}
                  onChange={(e) => setFocusNote(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                  onClick={handleGenerateDraft}
                  disabled={draftLoading || createLoading}
                >
                  {draftLoading ? 'Generating...' : aiDraft ? 'Regenerate Draft' : 'Generate Draft'}
                </button>
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-sm"
                  onClick={() => setAiDraftModalOpen(false)}
                  disabled={draftLoading || createLoading}
                >
                  Close
                </button>
              </div>

              {aiDraft && (
                <div className="pt-2 border-t border-gray-200 space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{aiDraft.lesson_title}</h3>
                    {aiDraft.objective && <p className="text-sm text-gray-700 mt-1">{aiDraft.objective}</p>}
                    {aiDraft.big_idea && <p className="text-sm text-gray-600 mt-1">{aiDraft.big_idea}</p>}
                  </div>

                  {STAGES.filter((stage) => Array.isArray(groupedDraftSteps[stage]) && groupedDraftSteps[stage].length > 0).map((stage) => (
                    <div key={stage} className="space-y-2">
                      <h4 className="text-xs uppercase tracking-[0.15em] text-gray-500">{stage}</h4>
                      {groupedDraftSteps[stage].map((step, idx) => (
                        <div key={`${stage}-${idx}-${step?.order}`} className="border border-gray-200 rounded p-3 space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium text-gray-900">{step?.order}. {step?.title}</p>
                            <span className="text-xs text-gray-500">{step?.step_kind}</span>
                          </div>
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{String(step?.content?.prompt_md || '')}</ReactMarkdown>
                          </div>
                          {step?.suggested_signal_config && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2">
                              <p className="text-xs font-semibold text-amber-800">Suggested thinking check</p>
                              <p className="text-xs text-amber-700 mt-1">Type: {String(step.suggested_signal_config?.type || '')}</p>
                              <p className="text-xs text-amber-700 mt-1">{String(step.suggested_signal_config?.prompt || '')}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="pt-2">
                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded text-sm"
                      onClick={handleCreateFromDraft}
                      disabled={createLoading || draftLoading || draftStepsSorted.length === 0}
                    >
                      {createLoading ? 'Creating...' : 'Create Lesson Plan from Draft'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={repositoryModalOpen}
        onClose={() => {
          if (!repositoryImporting) setRepositoryModalOpen(false);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Repository Lessons</Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                {repositoryProjectTitle
                  ? `${repositoryProjectTitle} curriculum for ${hook.title}`
                  : `Choose lesson plans to add to ${hook.title}.`}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <div className="mb-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Source repository</label>
                  <div className="mt-2 min-h-10 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    {repositorySourceLabel || 'Loading...'}
                  </div>
                </div>
                <div>
                  <label htmlFor="repository-project" className="block text-sm font-medium text-gray-700">Source project</label>
                  <Select
                    id="repository-project"
                    className="mt-2"
                    value={repositoryProjectKey}
                    disabled={repositoryLoading || repositoryImporting}
                    onChange={(event) => loadRepositoryCatalog(event.target.value)}
                  >
                    {repositoryProjects.map((project) => (
                      <option key={project.project_key} value={project.project_key}>
                        {project.project_title}{project.grade_level ? ` · Grade ${project.grade_level}` : ''}{project.school_year ? ` · ${project.school_year}` : ''} · {project.curriculum_status}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label htmlFor="repository-week" className="block text-sm font-medium text-gray-700">Week</label>
                  <Select
                    id="repository-week"
                    className="mt-2"
                    value={repositorySelectedWeek}
                    disabled={repositoryLoading || repositoryImporting || repositoryWeeks.length === 0}
                    onChange={(event) => {
                      const weekKey = event.target.value;
                      const weekLessons = repositoryWeeks.find((week) => week.key === weekKey)?.lessons || [];
                      setRepositorySelectedWeek(weekKey);
                      setRepositorySelectedKeys(weekLessons.filter((lesson) => lesson.can_add).map((lesson) => lesson.key));
                    }}
                  >
                    {repositoryWeeks.length === 0 ? <option value="">No weeks available</option> : null}
                    {repositoryWeeks.map((week) => (
                      <option key={week.key} value={week.key}>
                        {week.label} · {week.lessons.length} lesson{week.lessons.length === 1 ? '' : 's'}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              {repositoryError && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {repositoryError}
                </div>
              )}
              {repositoryLoading ? (
                <p className="text-sm text-gray-500">Loading repository lessons...</p>
              ) : repositoryLessons.length === 0 ? (
                <p className="text-sm text-gray-500">No repository lessons are available.</p>
              ) : visibleRepositoryLessons.length === 0 ? (
                <p className="text-sm text-gray-500">No repository lessons are available for this week.</p>
              ) : (
                <div>
                  {repositoryMeetings.filter((meeting) => !meeting.week_number || `week-${meeting.week_number}` === repositorySelectedWeek).map((meeting) => {
                    const availableKeys = meeting.items.filter((item) => item.can_add).map((item) => item.source_key);
                    return <section key={meeting.key} className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-medium text-gray-900">{meeting.title}</p><p className="text-xs text-gray-600">{meeting.items.map((item) => item.title).join(' → ')}</p></div><Button variant="secondary" size="sm" disabled={repositoryImporting || (!meeting.all_imported && availableKeys.length === 0)} onClick={() => meeting.all_imported ? void enableImportedMeeting(meeting) : setRepositorySelectedKeys((current) => Array.from(new Set([...current, ...availableKeys])))}>{meeting.all_imported ? 'Enable in daily plans' : 'Select meeting'}</Button></div>
                    </section>;
                  })}
                  <div className="mb-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={repositoryImporting}
                      onClick={() => setRepositorySelectedKeys(
                        visibleRepositoryLessons
                          .filter((lesson) => lesson.can_add)
                          .map((lesson) => lesson.key),
                      )}
                    >
                      Select all available
                    </Button>
                  </div>
                  <div className="divide-y divide-gray-200 border-y border-gray-200">
                  {visibleRepositoryLessons.map((lesson) => {
                    const selectable = lesson.can_add;
                    const selected = repositorySelectedKeys.includes(lesson.key);
                    return (
                      <div key={lesson.key} className="flex items-start gap-3 py-4">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
                          checked={selected}
                          disabled={!selectable || repositoryImporting}
                          aria-label={`${lesson.lesson_plan_archived ? 'Restore' : 'Add'} ${lesson.title}`}
                          onChange={(event) => {
                            setRepositorySelectedKeys((current) => event.target.checked
                              ? [...current, lesson.key]
                              : current.filter((key) => key !== lesson.key));
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{lesson.title}</p>
                            <Badge variant={lesson.curriculum_status === 'active' ? 'success' : 'muted'}>
                              {lesson.curriculum_status === 'active'
                                ? 'Active curriculum'
                                : lesson.curriculum_status === 'draft'
                                  ? 'Draft curriculum'
                                  : 'Archived curriculum'}
                            </Badge>
                            {lesson.lesson_status !== 'ready' && <Badge variant="muted">{lesson.lesson_status}</Badge>}
                            <Badge variant="muted">{lesson.content_format === 'activity' ? 'Activity' : 'Full lesson'}</Badge>
                            {lesson.activity_type && <Badge variant="muted">{lesson.activity_type.replace(/_/g, ' ')}</Badge>}
                            {lesson.status === 'current' && !lesson.lesson_plan_archived && <Badge variant="muted">Added</Badge>}
                            {lesson.lesson_plan_archived && <Badge variant="muted">Previously added</Badge>}
                            {lesson.status === 'update_available' && <Badge variant="warning">Update available</Badge>}
                            {lesson.lesson_plan_archived && <Badge variant="muted">Archived</Badge>}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {lesson.cycle} · {lesson.sequence.split('_').join(' ')} · {lesson.step_count} steps
                          </p>
                          {lesson.resource_summary.incoming_count > 0 ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                              <span>{lesson.resource_summary.incoming_count} material{lesson.resource_summary.incoming_count === 1 ? '' : 's'}</span>
                              {lesson.resource_summary.printable_count > 0 ? <span>· {lesson.resource_summary.printable_count} need printing</span> : null}
                              {lesson.resource_summary.source_types.map((sourceType) => (
                                <Badge key={sourceType} variant="muted">{lessonResourceSourceLabel(sourceType)}</Badge>
                              ))}
                            </div>
                          ) : null}
                          {lesson.description && <p className="mt-2 text-sm text-gray-600">{lesson.description}</p>}
                        </div>
                        <div className="flex shrink-0 flex-col gap-2">
                          {lesson.lesson_plan_id && lesson.status === 'update_available' ? (
                            <Button variant="secondary" size="sm" onClick={() => setRepositoryMaterialPreview(lesson)}>
                              Review lesson update
                            </Button>
                          ) : null}
                          {lesson.lesson_plan_id && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/v2/teacher/lesson-plan/${lesson.lesson_plan_id}/edit`)}>
                              Open
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-gray-200 p-5">
              <p className="text-sm text-gray-500">{repositorySelectedKeys.length} selected</p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setRepositoryModalOpen(false)} disabled={repositoryImporting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImportRepositoryLessons}
                  disabled={repositoryImporting || !repositoryProjectKey || repositorySelectedKeys.length === 0}
                >
                  {repositoryImporting ? 'Adding...' : 'Add selected'}
                </Button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(repositoryMaterialPreview)}
        onClose={() => {
          if (!repositorySyncing) setRepositoryMaterialPreview(null);
        }}
        className="relative z-[60]"
      >
        <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Review lesson update</Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">{repositoryMaterialPreview?.title}</p>
            </div>
            <div className="max-h-[65vh] space-y-4 overflow-y-auto p-5">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-950">
                Updating creates a fresh lesson revision from the repository and archives the current revision. Existing runs and historical work remain attached to the archived revision.
              </div>
              {!repositoryMaterialPreview?.resource_summary.has_changes ? (
                <p className="text-sm text-gray-600">The runner content changed without changes to its material list.</p>
              ) : null}
              {([
                ['Added', repositoryMaterialPreview?.resource_summary.added || []],
                ['Changed', repositoryMaterialPreview?.resource_summary.changed || []],
                ['Removed', repositoryMaterialPreview?.resource_summary.removed || []],
              ] as Array<[string, RepositoryResourcePreviewItem[]]>).map(([label, items]) => items.length > 0 ? (
                <section key={label}>
                  <h3 className="text-sm font-semibold text-gray-900">{label} ({items.length})</h3>
                  <div className="mt-2 space-y-2">
                    {items.map((item, index) => (
                      <div key={`${label}-${item.title}-${index}`} className="rounded border border-gray-200 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900">{item.title}</span>
                          <div className="flex flex-wrap gap-1">
                            <Badge variant="muted">{lessonResourceSourceLabel(item.source_type)}</Badge>
                            <Badge variant={item.audience === 'teacher' ? 'warning' : 'muted'}>
                              {item.audience === 'teacher' ? 'Teacher only' : item.audience === 'both' ? 'Teacher + students' : 'Students'}
                            </Badge>
                            {item.needs_printing ? <Badge variant="warning">Needs printing</Badge> : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null)}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 p-5">
              <Button variant="secondary" disabled={repositorySyncing} onClick={() => setRepositoryMaterialPreview(null)}>Cancel</Button>
              <Button disabled={repositorySyncing} onClick={handleUpdateRepositoryLesson}>
                {repositorySyncing ? 'Updating...' : 'Update lesson'}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog
        open={importModalOpen}
        onClose={() => {
          if (!importLoading) {
            resetImportModal();
            setImportModalOpen(false);
          }
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="p-5 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Import Lesson Plan</Dialog.Title>
              <p className="text-sm text-gray-500 mt-1">Upload a JSON file or paste a LessonPlanImportBundle payload.</p>
            </div>

            <div className="p-5 space-y-4">
              {importError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {importError}
                </div>
              )}
              {importWarnings.length > 0 && importedPlanId && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded space-y-2">
                  <p className="font-medium">Imported with warnings</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {importWarnings.map((warning, idx) => (
                      <li key={`${idx}-${warning}`}>{warning}</li>
                    ))}
                  </ul>
                  <div className="pt-1">
                    <button
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm"
                      onClick={() => {
                        setImportModalOpen(false);
                        navigate(`/v2/teacher/lesson-plan/${importedPlanId}/edit`);
                      }}
                    >
                      Open Plan Editor
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">JSON file</label>
                <input
                  className="mt-2 block w-full text-sm"
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                  disabled={importLoading || Boolean(importedPlanId)}
                />
                <p className="text-xs text-gray-500 mt-1">UTF-8 BOM files are supported.</p>
              </div>

              <div className="text-sm text-gray-500">or paste bundle JSON</div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bundle JSON</label>
                <textarea
                  className="mt-2 w-full border rounded px-3 py-2 text-sm font-mono h-48"
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='{"bundle_type":"LessonPlanImportBundle","bundle_version":"v2","plan":{...},"steps":[...]}'
                  disabled={importLoading || Boolean(importedPlanId)}
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-sm"
                  onClick={() => {
                    resetImportModal();
                    setImportModalOpen(false);
                  }}
                  disabled={importLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                  onClick={handleImportBundle}
                  disabled={importLoading || Boolean(importedPlanId)}
                >
                  {importLoading ? 'Importing...' : 'Import'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default V2PlanHookDetail;
